import { Trash2 } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { useAppDispatch } from "../../app/hooks";
import { Message } from "../../features/messages/models/Message";
import { MessagesChannel } from "../../features/messages/models/MessagesChannel";
import { deleteMessage } from "../../features/messages/slice-extensions/messages-slice-extension";
import ConfirmationModal from "../ConfirmationModal";

interface DeleteMessageButtonProps {
  channel: MessagesChannel;
  message: Message;
}

/** The quiet row action that deletes a message after a confirm; the row stays as a placeholder. */
const DeleteMessageButton = ({ channel, message }: DeleteMessageButtonProps) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);

  const confirm = async () => {
    setBusy(true);
    const result = await dispatch(deleteMessage({ channel, threadId: message.threadId, messageId: message.id }));
    setBusy(false);
    setConfirming(false);
    if (deleteMessage.rejected.match(result)) {
      toast.error(result.payload?.statusCode === 409 ? t("messages.deleteTooLate") : t("messages.deleteFailed"));
    }
  };

  return (
    <>
      <button
        type="button"
        className="ml-auto rounded p-1 text-grey-40 hover:text-red-700 dark:text-grey-80 dark:hover:text-red-400"
        onClick={() => setConfirming(true)}
        aria-label={t("messages.delete")}
        title={t("messages.delete")}
      >
        <Trash2 size={14} aria-hidden="true" />
      </button>
      <ConfirmationModal
        isOpen={confirming}
        title={t("messages.deleteConfirm.title")}
        message={t("messages.deleteConfirm.body")}
        confirmText={t("messages.delete")}
        cancelText={t("messages.deleteConfirm.keep")}
        variant="danger"
        confirmVariant="danger"
        confirmPending={busy}
        onConfirm={confirm}
        onCancel={() => setConfirming(false)}
      />
    </>
  );
};

export default DeleteMessageButton;
